import { useContext } from 'react'
import { useSelector } from 'react-redux'

import cloneDeep from 'lodash/cloneDeep'

import PageContext from 'contexts/pageContext'
import defaultState, { requiredFields } from 'models/project'
import useForm from 'hooks/useForm'
import { useLatestEntity, useThunkDispatch } from '@contenthouse-io/hooks'

import * as projectActions from 'redux/modules/project'

const createProject = (projectParams, dispatch, requestOptions) => {
  const { createProject: createFn } = projectActions
  return dispatch(createFn(projectParams, requestOptions))
}

const createProjectPublicLink = (projectParams, dispatch, requestOptions) => {
  const { createProjectPublicLink: createPublicLinkFn } = projectActions
  return dispatch(createPublicLinkFn(projectParams, requestOptions))
}

const deleteProjectPublicLink = (projectParams, dispatch, requestOptions) => {
  const { deleteProjectPublicLink: deletePublicLinkFn } = projectActions
  return dispatch(deletePublicLinkFn(projectParams, requestOptions))
}

const updateProject = (projectParams, dispatch, requestOptions) => {
  const { updateProject: updateFn } = projectActions
  return dispatch(updateFn(projectParams, requestOptions))
}

const patchProject = (projectParams, dispatch, requestOptions) => {
  const { patchProject: patchFn } = projectActions
  return dispatch(patchFn(projectParams, requestOptions))
}

const createOrEditProject = (project, dispatch, showProjectModal) =>
  new Promise((resolve, reject) => {
    if (showProjectModal) {
      const payload = {
        project,
      }

      showProjectModal(payload)

      return resolve({ success: true, result: payload })
    }

    return reject(new Error('showProjectModal not defined in PageContext callbacks'))
  })

export function useProjectForm(project = {}) {
  const projectForm = useForm(defaultState, { entity: { ...defaultState, ...project }, requiredFields, validateOn: 'change' }, [
    project.id,
    project.cache_key,
  ])
  const initialValue = Object.keys(project).length === 0 ? defaultState : cloneDeep(projectForm.entityState)
  return {
    ...projectForm,
    initialValue,
  }
}

const useProject = (initProject = {}) => {
  const { entity: project } = useLatestEntity(initProject, 'projects')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showProjectModal } = callbacks || {}

  const { creating, deleting, loading, updating } = useSelector((reduxState) => reduxState.projects)

  return {
    project,
    callbacks: {
      createProject: (params, requestOptions) => createProject(params, dispatch, requestOptions),
      createProjectPublicLink: (params, requestOptions) => createProjectPublicLink(params, dispatch, requestOptions),
      deleteProjectPublicLink: (params, requestOptions) => deleteProjectPublicLink(params, dispatch, requestOptions),
      updateProject: (params, requestOptions) => updateProject(params, dispatch, requestOptions),
      patchProject: (params, requestOptions) => patchProject(params, dispatch, requestOptions),
      createOrEditProject: (params) => createOrEditProject(params || project, dispatch, showProjectModal),
    },
    creating,
    deleting,
    loading,
    updating,
  }
}

export default useProject
